import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import AddIcon from "@material-ui/icons/Add";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import ForumIcon from "@material-ui/icons/Forum";
import SearchIcon from "@material-ui/icons/Search";

import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import QuickMessageDialog from "../../components/QuickMessageDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_QUICKMESSAGES") {
    const quickmessages = action.payload;
    const newQuickmessages = [];

    if (isArray(quickmessages)) {
      quickmessages.forEach((quickemessage) => {
        const quickemessageIndex = state.findIndex(
          (u) => u.id === quickemessage.id
        );
        if (quickemessageIndex !== -1) {
          state[quickemessageIndex] = quickemessage;
        } else {
          newQuickmessages.push(quickemessage);
        }
      });
    }

    return [...state, ...newQuickmessages];
  }

  if (action.type === "UPDATE_QUICKMESSAGES") {
    const quickemessage = action.payload;
    const quickemessageIndex = state.findIndex((u) => u.id === quickemessage.id);

    if (quickemessageIndex !== -1) {
      state[quickemessageIndex] = quickemessage;
      return [...state];
    }
    return [quickemessage, ...state];
  }

  if (action.type === "DELETE_QUICKMESSAGE") {
    const quickemessageId = action.payload;
    const quickemessageIndex = state.findIndex((u) => u.id === quickemessageId);
    if (quickemessageIndex !== -1) {
      state.splice(quickemessageIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useStyles = makeStyles((theme) => {
  const isDark = theme.mode === "dark";
  const primary = theme.palette.primary.main;

  return {
    mainPaper: {
      flex: 1,
      padding: 0,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      border: "none",
      borderRadius: 10,
      background: isDark ? "#424242" : "#f2f3ff",
      boxShadow: isDark
        ? "0 18px 42px rgba(0, 0, 0, 0.28)"
        : "0 18px 42px rgba(15, 23, 42, 0.08)",
    },
    overview: {
      padding: theme.spacing(3),
      background: isDark
        ? "#424242"
        : "linear-gradient(135deg, #ffffff 0%, #f2f6ff 58%, #e8efff 100%)",
      color: isDark ? "#f8fafc" : "#131b2e",
    },
    overviewTop: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
      },
    },
    overviewTitleRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
    },
    overviewIcon: {
      width: 42,
      height: 42,
      borderRadius: 10,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#ffffff",
      background: `linear-gradient(135deg, ${primary} 0%, #0052cc 100%)`,
      boxShadow: isDark
        ? "0 12px 24px rgba(0, 0, 0, 0.28)"
        : "0 12px 24px rgba(0, 82, 204, 0.22)",
    },
    overviewLabel: {
      color: isDark ? "#93c5fd" : "#4c5d8d",
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.5,
      textTransform: "uppercase",
    },
    overviewTitle: {
      margin: 0,
      color: isDark ? "#f8fafc" : "#131b2e",
      fontSize: 28,
      lineHeight: 1.12,
      fontWeight: 800,
    },
    overviewText: {
      maxWidth: 680,
      margin: theme.spacing(1.5, 0, 0),
      color: isDark ? "#e5e7eb" : "#434654",
      fontSize: 14,
      lineHeight: 1.6,
    },
    addButton: {
      minHeight: 42,
      padding: theme.spacing(1, 2),
      borderRadius: 6,
      fontWeight: 700,
      textTransform: "none",
      color: "#ffffff",
      background: `linear-gradient(135deg, ${primary} 0%, #0052cc 100%)`,
      boxShadow: isDark
        ? "0 12px 24px rgba(0, 0, 0, 0.28)"
        : "0 12px 24px rgba(0, 82, 204, 0.24)",
      "&:hover": {
        background: isDark
          ? "linear-gradient(135deg, #39ace7 0%, #0052cc 100%)"
          : "linear-gradient(135deg, #00327f 0%, #0049b8 100%)",
      },
    },
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: theme.spacing(1.5),
      marginTop: theme.spacing(3),
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "1fr",
      },
    },
    statCard: {
      minHeight: 84,
      padding: theme.spacing(1.5),
      borderRadius: 8,
      backgroundColor: isDark ? "#4e4e4e" : "#ffffff",
      boxShadow: isDark
        ? "0 12px 30px rgba(0, 0, 0, 0.2)"
        : "0 12px 30px rgba(19, 27, 46, 0.06)",
    },
    statHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      color: isDark ? "#93c5fd" : "#4c5d8d",
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.35,
      textTransform: "uppercase",
    },
    statIcon: {
      width: 30,
      height: 30,
      borderRadius: 8,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: isDark ? "#ffffff" : "#0040a2",
      backgroundColor: isDark ? "rgba(0, 82, 204, 0.32)" : "#dae2ff",
    },
    statValue: {
      marginTop: theme.spacing(1),
      color: isDark ? "#ffffff" : "#131b2e",
      fontSize: 26,
      lineHeight: 1,
      fontWeight: 800,
    },
    statHint: {
      marginTop: theme.spacing(0.75),
      color: isDark ? "#d1d5db" : "#737685",
      fontSize: 12,
    },
    content: {
      flex: 1,
      minHeight: 0,
      padding: theme.spacing(2),
      overflowY: "auto",
      backgroundColor: isDark ? "#424242" : "#f2f3ff",
      ...theme.scrollbarStyles,
    },
    toolbar: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down("sm")]: {
        alignItems: "stretch",
        flexDirection: "column",
      },
    },
    searchField: {
      flex: 1,
      "& .MuiOutlinedInput-root": {
        borderRadius: 8,
        color: isDark ? "#ffffff" : "#131b2e",
        backgroundColor: isDark ? "#4e4e4e" : "#ffffff",
        "& fieldset": {
          borderColor: isDark
            ? "rgba(255, 255, 255, 0.22)"
            : "rgba(195, 198, 214, 0.55)",
        },
        "&:hover fieldset": {
          borderColor: isDark
            ? "rgba(147, 197, 253, 0.72)"
            : "rgba(0, 82, 204, 0.55)",
        },
        "&.Mui-focused fieldset": {
          borderColor: primary,
          borderWidth: 1,
        },
      },
      "& .MuiInputAdornment-root .MuiSvgIcon-root": {
        color: isDark ? "#93c5fd" : "inherit",
      },
    },
    filterChip: {
      height: 38,
      borderRadius: 999,
      backgroundColor: isDark ? "#0052cc" : "#e2e7ff",
      color: isDark ? "#ffffff" : "#344573",
      fontWeight: 700,
      "& .MuiChip-icon": {
        color: isDark ? "#ffffff" : "#344573",
      },
    },
    tableContainer: {
      borderRadius: 8,
      backgroundColor: isDark ? "#4a4a4a" : "#ffffff",
      boxShadow: isDark
        ? "0 14px 34px rgba(0, 0, 0, 0.24)"
        : "0 12px 30px rgba(19, 27, 46, 0.06)",
    },
    table: {
      minWidth: 860,
      borderCollapse: "separate",
      borderSpacing: "0 4px",
    },
    headerCell: {
      borderBottom: "none",
      color: isDark ? "#93c5fd" : "#4c5d8d",
      backgroundColor: isDark ? "#4a4a4a" : "#ffffff",
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    row: {
      backgroundColor: isDark ? "#505050" : "#ffffff",
      transition: "background-color 180ms ease",
      "&:nth-of-type(even)": {
        backgroundColor: isDark ? "#4b4b4b" : "#f7f9ff",
      },
      "&:hover": {
        backgroundColor: isDark ? "#575757" : "#eef4ff",
      },
      "& td": {
        borderBottom: "none",
        color: isDark ? "#e5e7eb" : "inherit",
        paddingTop: theme.spacing(1.35),
        paddingBottom: theme.spacing(1.35),
      },
    },
    shortcodeChip: {
      maxWidth: 220,
      color: isDark ? "#ffffff" : "#0040a2",
      backgroundColor: isDark ? "#0052cc" : "#dae2ff",
      fontWeight: 800,
      "& .MuiChip-label": {
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },
    mediaChip: {
      color: isDark ? "#e0e7ff" : "#415382",
      backgroundColor: isDark ? "rgba(0, 82, 204, 0.45)" : "#e2e7ff",
      fontWeight: 700,
    },
    messageCell: {
      maxWidth: 520,
    },
    messagePreview: {
      display: "-webkit-box",
      overflow: "hidden",
      color: isDark ? "#e5e7eb" : "#434654",
      lineHeight: 1.45,
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
    },
    statusChip: {
      color: isDark ? "#dcfce7" : "#166534",
      backgroundColor: isDark ? "rgba(34, 197, 94, 0.18)" : "#dcfce7",
      fontWeight: 800,
      "& .MuiChip-icon": {
        color: isDark ? "#86efac" : "#16a34a",
      },
    },
    emptyCell: {
      borderBottom: "none",
      padding: theme.spacing(6, 2),
    },
    emptyState: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing(1),
      color: isDark ? "#d1d5db" : "#737685",
      textAlign: "center",
    },
    emptyTitle: {
      color: isDark ? "#ffffff" : "#131b2e",
      fontSize: 16,
      fontWeight: 800,
    },
    emptyText: {
      maxWidth: 420,
      fontSize: 13,
      lineHeight: 1.5,
    },
    actionButton: {
      width: 34,
      height: 34,
      margin: theme.spacing(0, 0.25),
      backgroundColor: isDark ? "#424242" : "#ffffff",
      boxShadow: isDark
        ? "0 6px 16px rgba(0, 0, 0, 0.2)"
        : "0 6px 16px rgba(19, 27, 46, 0.08)",
    },
    editButton: {
      color: isDark ? "#7dd3fc" : "#0052cc",
      "&:hover": {
        backgroundColor: isDark ? "rgba(57, 172, 231, 0.18)" : "#dae2ff",
      },
    },
    deleteButton: {
      color: isDark ? "#fca5a5" : "#ba1a1a",
      "&:hover": {
        backgroundColor: isDark ? "rgba(239, 68, 68, 0.16)" : "#ffdad6",
      },
    },
  };
});

const Quickemessages = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedQuickemessage, setSelectedQuickemessage] = useState(null);
  const [deletingQuickemessage, setDeletingQuickemessage] = useState(null);
  const [quickemessageModalOpen, setQuickMessageDialogOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [quickemessages, dispatch] = useReducer(reducer, []);
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchQuickemessages();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;

    const onQuickMessageEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUICKMESSAGES", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUICKMESSAGE", payload: +data.id });
      }
    };
    socket.on(`company-${companyId}-quickemessage`, onQuickMessageEvent);

    return () => {
      socket.off(`company-${companyId}-quickemessage`, onQuickMessageEvent);
    };
  }, [socket, user.companyId]);

  const fetchQuickemessages = async () => {
    try {
      const { data } = await api.get("/quick-messages", {
        params: { searchParam, pageNumber },
      });

      dispatch({ type: "LOAD_QUICKMESSAGES", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleOpenQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(true);
  };

  const handleCloseQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(false);
    fetchQuickemessages();
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditQuickemessage = (quickemessage) => {
    setSelectedQuickemessage(quickemessage);
    setQuickMessageDialogOpen(true);
  };

  const handleDeleteQuickemessage = async (quickemessageId) => {
    try {
      await api.delete(`/quick-messages/${quickemessageId}`);
      toast.success(i18n.t("quickMessages.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingQuickemessage(null);
    setSearchParam("");
    setPageNumber(1);
    fetchQuickemessages();
    dispatch({ type: "RESET" });
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const mediaCount = quickemessages.filter((message) => message.mediaName || message.mediaPath).length;
  const globalCount = quickemessages.filter((message) => message.geral === true).length;
  const hasActiveFilter = Boolean(searchParam.trim());

  const renderRows = () => {
    if (!quickemessages.length && !loading) {
      return (
        <TableRow>
          <TableCell colSpan={5} className={classes.emptyCell}>
            <Box className={classes.emptyState}>
              <Box className={classes.emptyTitle}>Nenhuma resposta rapida encontrada</Box>
              <Box className={classes.emptyText}>
                Crie atalhos para mensagens frequentes ou ajuste a busca para encontrar respostas cadastradas.
              </Box>
            </Box>
          </TableCell>
        </TableRow>
      );
    }

    return quickemessages.map((quickemessage) => {
      const shortcode = quickemessage.shortcode || "Sem atalho";
      const message = quickemessage.message || "Sem mensagem cadastrada";
      const mediaName = quickemessage.mediaName || quickemessage.mediaPath;

      return (
        <TableRow key={quickemessage.id} className={classes.row}>
          <TableCell align="center">
            <Chip size="small" label={shortcode} className={classes.shortcodeChip} />
          </TableCell>
          <TableCell align="left" className={classes.messageCell}>
            <span className={classes.messagePreview} title={message}>
              {message}
            </span>
          </TableCell>
          <TableCell align="center">
            <Chip
              size="small"
              icon={mediaName ? <AttachFileIcon /> : undefined}
              label={mediaName || i18n.t("quickMessages.noAttachment")}
              className={classes.mediaChip}
            />
          </TableCell>
          <TableCell align="center">
            {quickemessage.geral === true ? (
              <Chip
                size="small"
                icon={<CheckCircleIcon />}
                label="Geral"
                className={classes.statusChip}
              />
            ) : (
              <Chip size="small" label="Individual" className={classes.mediaChip} />
            )}
          </TableCell>
          <TableCell align="center">
            <Tooltip title="Editar resposta">
              <IconButton
                size="small"
                onClick={() => handleEditQuickemessage(quickemessage)}
                className={`${classes.actionButton} ${classes.editButton}`}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir resposta">
              <IconButton
                size="small"
                onClick={() => {
                  setConfirmModalOpen(true);
                  setDeletingQuickemessage(quickemessage);
                }}
                className={`${classes.actionButton} ${classes.deleteButton}`}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingQuickemessage && `${i18n.t("quickMessages.confirmationModal.deleteTitle")} ${deletingQuickemessage.shortcode}?`}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteQuickemessage(deletingQuickemessage.id)}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QuickMessageDialog
        resetPagination={() => {
          setPageNumber(1);
          fetchQuickemessages();
        }}
        open={quickemessageModalOpen}
        onClose={handleCloseQuickMessageDialog}
        aria-labelledby="form-dialog-title"
        quickemessageId={selectedQuickemessage && selectedQuickemessage.id}
      />
      <Paper className={classes.mainPaper} variant="outlined">
        <Box className={classes.overview}>
          <Box className={classes.overviewTop}>
            <Box>
              <Box className={classes.overviewTitleRow}>
                <Box className={classes.overviewIcon}>
                  <FlashOnIcon />
                </Box>
                <Box>
                  <Box className={classes.overviewLabel}>Atalhos do atendimento</Box>
                  <h2 className={classes.overviewTitle}>{i18n.t("quickMessages.title")}</h2>
                </Box>
              </Box>
              <p className={classes.overviewText}>
                Centralize mensagens prontas, anexos e atalhos para responder conversas repetitivas com mais velocidade.
              </p>
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenQuickMessageDialog}
              className={classes.addButton}
            >
              {i18n.t("quickMessages.buttons.add")}
            </Button>
          </Box>

          <Box className={classes.statsGrid}>
            <Box className={classes.statCard}>
              <Box className={classes.statHeader}>
                Total de respostas
                <Box className={classes.statIcon}>
                  <ForumIcon fontSize="small" />
                </Box>
              </Box>
              <Box className={classes.statValue}>{quickemessages.length}</Box>
              <Box className={classes.statHint}>Itens carregados nesta consulta</Box>
            </Box>
            <Box className={classes.statCard}>
              <Box className={classes.statHeader}>
                Com anexo
                <Box className={classes.statIcon}>
                  <AttachFileIcon fontSize="small" />
                </Box>
              </Box>
              <Box className={classes.statValue}>{mediaCount}</Box>
              <Box className={classes.statHint}>Respostas com arquivo vinculado</Box>
            </Box>
            <Box className={classes.statCard}>
              <Box className={classes.statHeader}>
                Globais
                <Box className={classes.statIcon}>
                  <CheckCircleIcon fontSize="small" />
                </Box>
              </Box>
              <Box className={classes.statValue}>{globalCount}</Box>
              <Box className={classes.statHint}>Disponiveis para uso geral</Box>
            </Box>
          </Box>
        </Box>

        <Box className={classes.content} onScroll={handleScroll}>
          <Box className={classes.toolbar}>
            <TextField
              className={classes.searchField}
              fullWidth
              placeholder={i18n.t("quickMessages.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Chip
              className={classes.filterChip}
              icon={<SearchIcon />}
              label={hasActiveFilter ? `Filtro: ${searchParam}` : "Todas as respostas"}
            />
          </Box>

          <TableContainer className={classes.tableContainer}>
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell align="center" className={classes.headerCell}>
                    {i18n.t("quickMessages.table.shortcode")}
                  </TableCell>
                  <TableCell align="left" className={classes.headerCell}>
                    {i18n.t("quickMessages.table.message")}
                  </TableCell>
                  <TableCell align="center" className={classes.headerCell}>
                    {i18n.t("quickMessages.table.mediaName")}
                  </TableCell>
                  <TableCell align="center" className={classes.headerCell}>
                    {i18n.t("quickMessages.table.status")}
                  </TableCell>
                  <TableCell align="center" className={classes.headerCell}>
                    {i18n.t("quickMessages.table.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {renderRows()}
                {loading && <TableRowSkeleton columns={5} />}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>
    </MainContainer>
  );
};

export default Quickemessages;
